import { ComponentFactoryResolver } from "@angular/core";
import type { IAngularEvent } from "angular";
import deepcopy from "deepcopy";
import { Observable, Subject } from "rxjs";
import { ApiResponse } from "src/AngularJS/Models/api-response.model";
import { DashboardModuleMap, DashboardModuleResponsiveInstance, ModuleInfo } from "src/shared/models/module-info.model";
import { DashboardManager } from "../models/dashboard-manager";
import { ActiveDashboardModel, Dashboard, DashboardSelectionListApiModel, DashboardGroupStateDto, DashboardRecipientGroup, DashboardResponsiveApiModel, DashboardResponsiveApiModelSparse, DashboardsResponsiveExistingApiModel, IDashboardAssignments, IDashboardModules, NewModule, SaveDashboardModalData, SystemTemplateWto, WorkspaceNewTabViewMode, WorkspaceTemplateViewMode } from "../models/dashboard.model";
import { ModuleManagerExportFormat } from "../models/module-manager/module-manager-options.model";
import { ModuleManagerDateRangeInfo, WorkspaceApiModel } from "../models/module-manager/module-manager-settings.models";
import { ModuleManagerTransferData } from "../models/module-manager/module-manager-transfer-data.model";
import { SharedEventEmitter } from "../models/shared-event-emitter";
import SlideoutSettingsManager from "../models/slideout-settings-manager";
import { ClearViewUtils } from "../utils";
import { AngularModuleServiceBase } from "./angular-module-service-base";
import { CvApiServiceBase } from "./cv-api-service-base";
import { CvConfigurationServiceBase } from "./cv-configuration-service-base";
import { ModuleExportFormat } from "src/AngularJS/Dashboard/Models/module-export-format.model";
import { DashboardAssignmentChangesModel } from "src/AngularJS/Dashboard/dashboard-assignment.model";

export type ModuleResizeEventArgs = {
    dashboardKey: string;
    event: IAngularEvent;
    $element: JQLite;
    widget: Record<string, any>;
};

export class DashboardServiceBase {
    /** Maximum allowed modules per dashboard. Keep in-sync value in DashboardApiUtil.cs */
    readonly MAX_MODULES = 20; // not static because we don't have access to the class in javascript
    readonly GRIDSTER_COLUMNS = 32; // keep in sync with ClearView WebPortal\AppCode\models\DashboardApiModel.cs
    dashboardsLoading = false;
    loadExistingDashboardsFailed = false;
    loadExistingTemplatesFailed = false;
    permissions: { [key: string]: boolean };
    dashboardLoaded: { [key: string]: boolean } = {};

    /** Dashboard state key for the taskbar */
    taskbarKey = '';

    /** list of module keys that are currently on the taskbar */
    taskbarModules: DashboardModuleResponsiveInstance[] = [];

    /** holds module data while transferring between dashboards and the taskbar */
    moduleTransferData: { [moduleKey: string]: ModuleManagerTransferData } = {};

    /** All existing dashboards available to the user */
    availableDashboards: DashboardResponsiveApiModelSparse[] = [];

    /** All available templates available to the user */
    availableTemplates: DashboardResponsiveApiModelSparse[] = null;

    /** All available system templates available to the user */
    availableSystemTemplates: SystemTemplateWto[] = null;

    /** 'Use' Template */
    openFromTemplate = null;

    /** All responsive dashboard groups available to the user */
    dashboardGroups: string[] = [];

    /** The dashboard objects loaded in the workspace */
    dashboards: Dashboard[] = [];

    /** Represents the active dashboard in the CXone environment. */
    cxoneActiveDashboard: DashboardResponsiveApiModel = null;

    /** A subject that updates anytime available dashboards are updated */
    availableDashboardsChanged = new SharedEventEmitter<void>();

    newDashboardCreated = new SharedEventEmitter<void>();

    /** The number of dashboards allowed to be in the workspace. */
    maxAttachedDashboards = 0;

    /** A dictionary of information about exports */
    exportFormats: { [name: string]: ModuleExportFormat } = {
        csv: {
            description: 'CSV',
            mime: 'text/csv',
            extension: 'csv',
        },
        excel: {
            description: 'Excel',
            mime: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
            extension: 'xlsx',
        },
        excelNoObjectives: {
            description: 'Excel',
            mime: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
            extension: 'xlsx',
        },
    };

    dashboardCopied = new Subject<Dashboard>();
    dashboardAssignClosed = new Subject<void>();
    discardTemporaryDashboardEvent = new Subject<string>();

    /** the index and key of the active dashboard in the workspace */
    activeDashboard: ActiveDashboardModel = {
        index: 0,
        key: null,
    };

    newDashboardPreviousView = WorkspaceNewTabViewMode.Landing;
    newTabSelectionView = WorkspaceNewTabViewMode.Landing;
    templateView = WorkspaceTemplateViewMode.Landing;

    workspaceKey: string = null;
    useNewSettings: boolean = null;
    isNewPlatform = false;
    draggingModule: ModuleInfo = null;
    addModuleEvent = new Subject<ModuleInfo>();

    newTemplateDetails = {
        dashboardName: "",
        dashboardGroup: "",
        dashboardDescription: "",
        readyToBeSubmitted: false
    };

    previewType = "";
    selectedTemplate: DashboardResponsiveApiModelSparse = null;
    selectedSystemTemplate: SystemTemplateWto = null;
    activeLoads = [];

    // [activatedKey, deactivatedKey]
    activeChanged = new Subject<[string, string]>();
    reloadDashboards = new Subject<[string[], DashboardAssignmentChangesModel[]]>();
    requestPublishNeeded = new Subject<string>();
    refreshTimeZoneInfo = new Subject<string>();
    dashboardManagers = new Map<string, DashboardManager>();
    dashboardLoadFailed = new Subject<string>();
    cancelPublish = new Subject<void>();

    isSharedURL = false;

    public isDashboardProjection = false;
    public isDashboardWindow = false; //whether view dashboard in new window was used

    private _workspacePromise: Promise<WorkspaceApiModel> = null;
    private $injector: ng.auto.IInjectorService;
    private componentFactoryResolver: ComponentFactoryResolver;
    private _sharedTemplatePromise: Promise<ApiResponse<DashboardResponsiveApiModelSparse[]>> = null;
    private _systemTemplatePromise: Promise<ApiResponse<SystemTemplateWto[]>> = null;

    constructor(
        fromSharedService: boolean = false,
        protected cvApiService?: CvApiServiceBase,
        private angularModuleService?: AngularModuleServiceBase,
        private configService?: CvConfigurationServiceBase,
        private $window?: Window,
    ) {
        if (fromSharedService)
            return DashboardServiceBase.getServiceBase();

        if (!$window)
            this.$window = $window = window;
        if (!cvApiService)
            this.cvApiService = CvApiServiceBase.getServiceBase();
        if (!angularModuleService)
            this.angularModuleService = AngularModuleServiceBase.getServiceBase();
        if (!configService)
            this.configService = CvConfigurationServiceBase.getServiceBase();
        if ($window['Chart']) //Chart being called breaks things when testing
            $window['Chart'].defaults.set('plugins.datalabels', { display: false });
        this.isSharedURL = window.location.pathname.includes('SharedDashboard.aspx');

        this.cvApiService.apiTokenSubject.subscribe(token => {
            if (token != null) {
                this._workspacePromise = null;
                requestAnimationFrame(() => {
                    this.getUserWorkspace(); // give components a chance to set isCxOne = true
                    // cxone dashboards ended up calling load twice since it is also called in dashboard-header.component which is used by cxone dashbaords which is why this is needed
                    if (!this.configService.isCxOne) {
                        // used for PM dashboard slideout on non dashboard pages
                        this.loadExistingDashboardsAndGroups();
                    }
                });
            }
        });
    }

    static getServiceBase(): DashboardServiceBase {
        return ClearViewUtils.getSharedService(DashboardServiceBase, 'DashboardServiceBase');
    }

    async checkIsAdmin() {
        const response = await this.cvApiService.get<boolean>('/api/v2/dashboard/clearance');
        if (response.status_code === 200)
            return response.data;

        throw response;
    }

    async createDashboard(dashboardListItemModel: DashboardSelectionListApiModel) {
        const response = await this.cvApiService.post<DashboardSelectionListApiModel>('/api/v2/dashboard/create-shared-from-selection', dashboardListItemModel);
        if (response.status_code === 200)
            return response.data;

        throw response;
    }

    isDashboardAlreadyLoaded(dashboardKey: string): boolean {
        return !!this.dashboardLoaded[dashboardKey];
    };

    async loadDashboard(dashboardKey: string) {
        dashboardKey = dashboardKey || "`";

        const exists = this.activeLoads.includes(dashboardKey);
        if (!exists) {
            this.activeLoads.push(dashboardKey);
        }

        try {
            const response = await this.cvApiService.get<DashboardResponsiveApiModel>(`/api/v2/dashboard/${dashboardKey}`);
            if (response.status_code === 200) {
                //If dashboard exists in the list already, update it with the new values. Currently only timezone does this. Maybe add more properties in the future?
                //Can't update the whole dashboard object here because it will trigger a reload, making this an infinite loop.
                const dashboardMatch = this.dashboards.find(d => d?.DashboardID === response.data.dashboard.DashboardID);
                if (dashboardMatch != null)
                    dashboardMatch.TimeZoneInfoName = response.data.dashboard.TimeZoneInfoName;

                if (response.data.dashboard.IsCXOne)
                    this.cxoneActiveDashboard = response.data;
                this.dashboardLoaded[dashboardKey] = true;
                return response.data;
            } else {
                this.dashboardLoadFailed.next(dashboardKey);
                throw response;
            }
        }
        finally {
            const index = this.activeLoads.indexOf(dashboardKey);
            if (index > -1) {
                this.activeLoads.splice(index, 1);
            }
        }
    }

    async loadDashboardShareInfo(dashboardKey: string): Promise<DashboardResponsiveApiModelSparse> {
        const response = await this.cvApiService.get<DashboardResponsiveApiModelSparse>(`/api/v2/dashboard/${dashboardKey}/share-info`);
            
        if (response.status_code === 200) {
            return response.data;
        } 
        else {
            throw response;
        }
    }

    async loadWindowedDashboard(dashboardKey: string) {
        dashboardKey = dashboardKey || "`";

        const exists = this.activeLoads.includes(dashboardKey);
        if (!exists) {
            this.activeLoads.push(dashboardKey);
        }

        try {
            const response = await this.cvApiService.get<DashboardResponsiveApiModel>(`/api/v2/dashboard-window/${dashboardKey}`);
            if (response.status_code === 200) {
                return response.data;
            } else {
                this.dashboardLoadFailed.next(dashboardKey);
                throw response;
            }
        }
        finally {
            const index = this.activeLoads.indexOf(dashboardKey);
            if (index > -1) {
                this.activeLoads.splice(index, 1);
            }
        }
    }

    async loadTemplate(dashboardKey: string, ownerId: number) {
        dashboardKey = dashboardKey || "`";
        const response = await this.cvApiService.get<DashboardResponsiveApiModel>(`/api/v2/dashboard/${dashboardKey}/template/${ownerId}`);
        if (response.status_code === 200) {
            return response.data;
        } else {
            throw response;
        }
    }

    async loadSystemTemplate(dashboardKey: string) {
        dashboardKey = dashboardKey || "`";
        const uri = `/api/v2/dashboard/${dashboardKey}/systemtemplate`;
        const response = await this.cvApiService.get<DashboardModuleResponsiveInstance[]>(uri);
        if (response.status_code === 200) {
            return response.data;
        } else {
            throw response;
        }
    }

    async saveAsNewSystemTemplate(
        dashboardKey: string,
        personaId: number,
        dashboardName: string,
        dashboardGroup: string,
        dashboardDescription: string
    ) {
        const details = {
            Name: dashboardName,
            Group: dashboardGroup,
            Description: dashboardDescription,
            OwnerID: personaId
        };

        const response = await this.cvApiService.post<Dashboard>(`/api/v2/saveasnewsystemtemplate/${dashboardKey}`, details);
        if (response.status_code === 200) { // Success
            return response.data;
        } else {
            throw response;
        }
    }

    async createNewDashboard(
        workspaceKey: string,
        dashboardName: string,
        dashboardGroup: string,
        dashboardDescription: string,
        isCXOne?: boolean
    ) {
        const details = {
            Name: dashboardName,
            Group: dashboardGroup,
            Description: dashboardDescription,
            IsCXOne: isCXOne
        };

        const response = await this.cvApiService.post<Dashboard>(`/api/v2/newDashboard/${workspaceKey}`, details);
        if (response.status_code === 200) { // Success
            if (response.data !== null) {
                this.addDashboardToWorkspace(workspaceKey, response.data, false);
                this.availableDashboards.push(response.data as DashboardResponsiveApiModelSparse);
                this.availableDashboardsChanged.emit();
                return response.data;
            }
        } else {
            throw response;
        }
    }

    async saveDashboardToWorkspace(dashboardKey: string) {
        const response = await this.cvApiService.post(`/api/v2/workspace/dashboard/${dashboardKey}`);
        if (response.status_code === 200)
            return response;
        else
            throw response;
    }

    async addDashboardToWorkspace(workspaceKey: string, dashboard: Dashboard, save = false, setActive = true) {
        // Remove the new tab page if it is there (should be at the end)
        for (let i = this.dashboards.length - 1; i >= 0; i--) {
            if (this.dashboards[i] == null) {
                this.dashboards.splice(i, 1);
                break;
            }
        }

        // Check that there is room for the new dashboard
        if (this.dashboards.length >= this.maxAttachedDashboards)
            return false;

        if (dashboard.IsCXOne)
            this.clearDashboardsFromWorkspace();

        // Add the new dashboard to the list
        this.dashboards.push(dashboard);

        if (setActive) {
            // Set the new dashboard to active
            const prevDashboardKey = this.activeDashboard.key;
            this.setActiveDashboard(workspaceKey, dashboard.DashboardKey, true);
            this.triggerActiveDashboardChanged(dashboard.DashboardKey, prevDashboardKey);
        }

        if (save)
            await this.saveDashboardToWorkspace(dashboard.DashboardKey);
    }

    openSaveDashboardModal(data: SaveDashboardModalData, type: string, showFolder: boolean = true):
        Observable<DashboardResponsiveApiModelSparse> {
        throw Error("Not implemented"); // implemented in angular
    }

    async removeDashboard(workspaceKey: string, dashboardKey: string, deleteDashboard: boolean = false) {
        workspaceKey = workspaceKey ?? this.workspaceKey;

        if (!this.isDashboardInWorkspace(dashboardKey)) {
            if (deleteDashboard) {
                await this.deleteDashboard(dashboardKey);
            }
            return;
        }

        const response = await this.cvApiService.delete(`/api/v2/workspace/${workspaceKey}/dashboard/${dashboardKey}`);
        if (response.status_code === 200) {
            // Remove the dashboard from the dashboards list
            for (let i = 0; i < this.dashboards.length; i++) {
                const prevDashKey = i > 0 ? this.dashboards[i - 1].DashboardKey : null;
                if (this.dashboards[i] !== null && this.dashboards[i].DashboardKey === dashboardKey) {
                    // Remove the dashboard
                    this.dashboards.splice(i, 1);
                    if (i > 0) //If the first tab was removed, don't adjust the index (preventing index = -1 issues).
                        i--;

                    // If the active dashboard is being deleted, some things need to be checked.
                    //  1. If there is another dashboard available, set the previous dashboard as the active one.
                    //  2. If the only open (thus active) dashboard is being deleted, insert a null to show the "Open New/Existing" menu.

                    //Set active dashboard as necessary.
                    if (this.activeDashboard.key === dashboardKey) {
                        if (prevDashKey != null) {
                            this.setActiveDashboard(workspaceKey, prevDashKey, true);
                            this.triggerActiveDashboardChanged(prevDashKey, dashboardKey);
                        } else if (this.dashboards.length > 0) {
                            this.setActiveDashboard(workspaceKey, this.dashboards[0].DashboardKey, true);
                            this.triggerActiveDashboardChanged(this.dashboards[0].DashboardKey, dashboardKey);
                        }
                    } else if (this.activeDashboard.key === this.dashboards[i].DashboardKey) { // Update the active dashboard's index
                        this.activeDashboard.index = i;
                    } else if (this.activeDashboard.key === null) { // Update the index if the new tab page is the active dashboard
                        this.activeDashboard.index = this.dashboards.length - 1;
                    }

                    //If no dashboards are in the list, insert a null for the "Open New/Existing" menu.
                    if (this.dashboards.length === 0) {
                        this.dashboards.push(null);
                        this.activeDashboard = {
                            index: 0,
                            key: null,
                        };
                    }

                    break;
                }
            }
            // Check if the cxoneActiveDashboard has a matching key to the one to be deleted and set to null if so.
            if (this.cxoneActiveDashboard && this.cxoneActiveDashboard.dashboardKey === dashboardKey) {
                this.cxoneActiveDashboard = null;
            }

            if (deleteDashboard) {
                await this.deleteDashboard(dashboardKey);
            }
        } else {
            throw response;
        }
    }

    async deleteDashboard(dashboardKey: string) {
        const response = await this.cvApiService.delete(`/api/v2/dashboard/${dashboardKey}`);
        if (response.status_code === 200) {
            // Remove the dashboard from the list of available dashboards
            for (let i = 0; i < this.availableDashboards.length; i++) {
                if (this.availableDashboards[i].DashboardKey === dashboardKey) {
                    this.availableDashboards.splice(i, 1);
                    break;
                }
            }
        } else {
            throw response;
        }
    }

    async deleteMultipleDashboards(...dashboardKeys: string[]) {
        const response = await this.cvApiService.delete(`/api/v2/dashboardCollection/`, dashboardKeys);
        if (response.status_code === 200) {
            for (let i = 0; i < this.availableDashboards.length; i++) {
                for (const dashboardKey of dashboardKeys)
                    if (this.availableDashboards[i].DashboardKey === dashboardKey) {
                        this.availableDashboards.splice(i, 1);
                        break;
                    }
            }
        } else {
            throw response;
        }
    }

    createDashboardModule(moduleId: number, dashboardKey: string, relatedId: number) {
        const queryString = relatedId != null ? '?relatedId=' + relatedId : '';
        return this.cvApiService.get<NewModule>(`/api/v2/createModule/${moduleId}/dashboard/${dashboardKey}${queryString}`);
    }

    loadTaskbar() {
        return this.cvApiService.get<DashboardResponsiveApiModel>('/api/v2/dashboard/taskbar');
    }

    directiveExists(source: string) {
        const split = source.split('-');
        let directiveCamelCase = split[0];
        for (let i = 1; i < split.length; i++) {
            let word = split[i];
            word = word.substring(0, 1).toUpperCase() + word.substring(1);
            directiveCamelCase += word;
        }

        const angularComponent = this.angularModuleService.getAngularModule(source);

        // Only add the module type if its directive exists on the server
        const existsInAngularJS = this.$injector?.has(`${directiveCamelCase}Directive`);
        const existsInAngular = angularComponent != null;

        if (existsInAngular)
            return 'angular';
        else if (existsInAngularJS)
            return 'angularjs';
        else
            return false;
    }

    moveDashboardModule(moduleKey: string, sourceKey: string, destKey: string) {
        return this.cvApiService.get(`/api/v2/moveModule/${moduleKey}/source/${sourceKey}/dest/${destKey}`);
    }

    removeDashboardModule(moduleKey: string, dashboardKey: string) {
        return this.cvApiService.delete(`/api/v2/module/${moduleKey}/dashboard/${dashboardKey}`);
    }

    updateAllModuleMaps(dashboardKey: string, maps: DashboardModuleMap[]) {
        return this.cvApiService.post(`/api/v2/dashboard/${dashboardKey}/maps`, maps);
    }

    getUserWorkspace(isSharedURL: boolean = false, sharedKey = '') {
        if (this._workspacePromise != null)
            return this._workspacePromise;

        this._workspacePromise = new Promise(async (resolve, reject) => {
            try {
                let params = '?';

                if (this.configService.isCxOne)
                    params += 'isCxOne=true';
                if (isSharedURL)
                    params += `isSharedURL=true&sharedKey=${sharedKey}`;

                const response = await this.cvApiService.get<WorkspaceApiModel>(`/api/v2/userWorkspace${params}`);
                if (response.status_code === 200) {
                    // read workspace information
                    const workspace = response.data;
                    this.workspaceKey = workspace.WorkspaceKey;
                    this.maxAttachedDashboards = workspace.MaxDashboardCount;
                    this.permissions = workspace.UserPermissions;
                    this.useNewSettings = workspace.UseNewSettings;
                    this.isNewPlatform = workspace.IsNewPlatform;

                    if (workspace.ActiveDashboard === 'null')
                        workspace.ActiveDashboard = null;

                    // Add any dashboards attached to the workspace
                    if (workspace.Dashboards != null) {
                        // Make sure there aren't any null (including a default null) dashboards in the current collection.
                        this.dashboards = this.dashboards.filter(d => d != null);

                        // Append dashboards
                        for (const dashboard of workspace.Dashboards) {
                            this.dashboards.push(dashboard);
                            // If the workspace contained a placeholder dashboard, the meaning is to show the user the Create New Dashboard view.
                            if (dashboard === null)
                                this.newTabSelectionView = WorkspaceNewTabViewMode.New;
                        }

                        // Check if there's no dashboards. If no dashboards, push a null to open the New/Existing menu.
                        if (this.dashboards.length === 0)
                            this.dashboards.push(null);
                    }

                    this.setActiveDashboard(workspace.WorkspaceKey, workspace.ActiveDashboard, true);

                    resolve(workspace);
                } else {
                    reject(response);
                }
            } catch (error) {
                reject(error);
            }
        });
    }

    async saveActiveDashboard(workspaceKey: string) {
        const response = await this.cvApiService.put<Dashboard>(`/api/v2/workspace/${workspaceKey}/dashboard/${this.activeDashboard.key}`);
        if (response.status_code === 200)
            return response.data;
        else
            throw response;
    }

    async setActiveDashboard(workspaceKey: string, dashboardKey: string, save: boolean = false) {
        if (dashboardKey == null)
            save = false;

        let activeChanged = false;
        for (let i = 0; i < this.dashboards.length; i++) {
            const dashboard = this.dashboards[i];
            if (dashboard === null) { // New Tab Page
                this.activeDashboard.key = null;
                this.activeDashboard.index = i;
                activeChanged = true;
            } else if (dashboard.DashboardKey === dashboardKey) { // Attached Dashboard
                this.activeDashboard.key = dashboard.DashboardKey;
                this.activeDashboard.index = i;
                activeChanged = true;
                break;
            }
        }

        // The given active dashboard no longer exists in the workspace. Reset the active dashboard.
        if (!activeChanged && this.dashboards[0] !== undefined) {
            this.activeDashboard.key = this.dashboards[0].DashboardKey;
            this.activeDashboard.index = 0;
            save = true;
        }

        if (save && this.activeDashboard.key != null && !this.isSharedURL) {
            try {
                const dashboard = await this.saveActiveDashboard(workspaceKey);
                const index = this.activeDashboard.index;
                if (dashboard != null) {
                    this.dashboards[index].LastViewDate = dashboard?.LastViewDate;
                    this.synchronizeDashboard(dashboard);
                }
            }
            catch (error) {
                throw error;
            }
        }
    }

    triggerActiveDashboardChanged(newDashboardKey: string, oldDashboardKey: string) {
        this.activeChanged.next([newDashboardKey, oldDashboardKey]);
    }

    async loadCxoneDashboardDropdownList() {
        this.dashboardsLoading = true;
        try {
            const response = await this.cvApiService.get<DashboardSelectionListApiModel[]>('/api/v2/dashboards/list');
            if (response.status_code === 200) {
                return response.data;
            }
            else {
                throw response;
            }
        }
        catch (error) {
            throw error;
        }
        finally {
            this.dashboardsLoading = false;
        }
    }

    async loadExistingDashboardsAndGroups() {
        this.dashboardsLoading = true;
        try {
            const response = await this.cvApiService.get<DashboardsResponsiveExistingApiModel>('/api/v2/dashboards');
            if (response.status_code === 200) {
                this.dashboardGroups = [];
                this.availableDashboards = [];
                for (const group of response.data.Groups) {
                    if (group.toLowerCase() !== "taskbar")
                        this.dashboardGroups.push(group);
                }

                this.availableDashboards = response.data.Dashboards;

                // Remap logins to dashboard profiles
                const logins = response.data.Logins;
                this.availableDashboards.forEach((d) => {
                    if (!!d.RecipientGroups)
                        Object.keys(d.RecipientGroups).forEach((key) => {
                            let rg: DashboardRecipientGroup = d.RecipientGroups[key];
                            rg.Profiles?.forEach((profile) => {
                                if (!profile.relatedLogins)
                                    profile.relatedLogins = [];

                                profile.relatedLoginIds?.forEach((id) => {
                                    let login = logins[id];
                                    if (!!login)
                                        profile.relatedLogins.push(login);
                                });
                            });
                        });
                });

                this.loadExistingDashboardsFailed = false;
                return response;
            } else {
                throw response;
            }
        }
        catch (e) {
            this.loadExistingDashboardsFailed = true;
        } finally {
            this.dashboardsLoading = false;
            this.availableDashboardsChanged.emit();
        }
    }

    async loadSharedTemplates(forceReload = false): Promise<DashboardResponsiveApiModelSparse[]> {
        if (!forceReload && this.availableTemplates !== null)
            return this.availableTemplates;

        if (forceReload || this._sharedTemplatePromise === null)
            this._sharedTemplatePromise = this.cvApiService.get<DashboardResponsiveApiModelSparse[]>('/api/v2/dashboards/templates/');

        const response = await this._sharedTemplatePromise;
        if (response.status_code === 200) {
            this.availableTemplates = response.data;
            return response.data;
        } else {
            throw response;
        }
    }

    async loadSystemTemplates(forceReload = false, isCXOne = false, forManageList = false): Promise<SystemTemplateWto[]> {
        if (!forceReload && this.availableSystemTemplates !== null)
            return this.availableSystemTemplates;

        if (forceReload || this._systemTemplatePromise === null) {
            const uri = `/api/v2/dashboards/systemtemplates/getlatestsystemtemplate/${isCXOne}/${forManageList}`;
            this._systemTemplatePromise = this.cvApiService.get<SystemTemplateWto[]>(uri);
        }

        try {
            const response = await this._systemTemplatePromise;
            if (response.status_code === 200) {
                this.availableSystemTemplates = response.data;
                return response.data;
            } else {
                throw response;
            }
        } catch {
            this.loadExistingTemplatesFailed = true;
        }
    }

    async updateDashboardDetails(dashboardKey: string, dashboardName: string, dashboardGroup: string, dashboardDescription: string, iscxone?: boolean) {
        const details = {
            Name: dashboardName,
            Group: dashboardGroup,
            Description: dashboardDescription,
            IsCXOne: iscxone
        };

        const response = await this.cvApiService.post<Dashboard>(`/api/v2/dashboard/${dashboardKey}/details`, details);
        if (response.status_code === 200) { // Success
            if (response.data !== null) {
                const updatedDashboard = response.data;

                // Update the dashboard in the available dashboards list to reflect the change
                const availableDashboard = this.availableDashboards.find(dashboard => dashboard.DashboardKey === dashboardKey);

                if (availableDashboard !== null && availableDashboard !== undefined) {
                    availableDashboard.DashboardName = updatedDashboard.DashboardName;
                    availableDashboard.DashboardGroup = updatedDashboard.DashboardGroup;
                    availableDashboard.DashboardDescription = updatedDashboard.DashboardDescription;
                }

                // Update the dashboard in the attached dashboards list to reflect the change
                const attachedDashboard = this.dashboards.find(dashboard => dashboard !== null && dashboard.DashboardKey === dashboardKey);

                if (attachedDashboard !== null && attachedDashboard !== undefined) {
                    attachedDashboard.DashboardName = updatedDashboard.DashboardName;
                    attachedDashboard.DashboardGroup = updatedDashboard.DashboardGroup;
                    attachedDashboard.DashboardDescription = updatedDashboard.DashboardDescription;
                }

                // Add the group to the dashboardGroups array if it doesn't already exist
                let groupExists = false;
                for (const group of this.dashboardGroups) {
                    if (group === updatedDashboard.DashboardGroup) {
                        groupExists = true;
                        break;
                    }
                }

                if (!groupExists)
                    this.dashboardGroups.push(updatedDashboard.DashboardGroup);

                this.availableDashboardsChanged.emit();
                return updatedDashboard;
            }
        } else {
            throw response;
        }
    }

    async saveGroupCollapsedState(groupName: string, isCollapsed: boolean) {
        const response = await this.cvApiService.post<void>('/api/v2/group/collapsed', { groupName, isCollapsed });
        if (response.status_code !== 200)
            throw response;
    }

    async getGroupInfo(groupName: string) {
        const response = await this.cvApiService.post<DashboardGroupStateDto>('/api/v2/group', JSON.stringify(groupName));
        if (response.status_code === 200 && response.data != null)
            return response.data;
        else
            throw response;
    }

    async deleteGroupInfo(groupName: string) {
        const response = await this.cvApiService.post<void>('/api/v2/group/state', JSON.stringify(groupName));
        if (response.status_code === 200) {
            // Remove the group from the factory object if it exists
            const index = this.dashboardGroups.findIndex(group => group === groupName);
            if (index >= 0)
                this.dashboardGroups.splice(index, 1);
        } else {
            throw response;
        }
    }

    async copyDashboard(dashboardKey: string) {
        const response = await this.cvApiService.get<DashboardResponsiveApiModelSparse>(`/api/v2/dashboardCopy/${dashboardKey}`);
        if (response.status_code === 200) {
            this.availableDashboards.push(response.data);
        } else {
            throw response;
        }
    }

    async copyDashboardWithParams(key: string, name: string, folder: string, description: string, iscxone?: boolean, workspaceKey?: string) {
        const response = await this.cvApiService.put<DashboardResponsiveApiModelSparse>(`/api/v2/dashboardCopyWithParams`, {
            Key: key,
            Name: name,
            Group: folder,
            Description: description,
            IsCXOne: iscxone,
            WorkspaceKey: iscxone ? null : this.workspaceKey
        });

        if (response.status_code === 200) {
            this.availableDashboards.unshift(response.data);
            this.availableDashboardsChanged.emit();
            return response.data;
        } else {
            throw response;
        }

        return response.data;
    }

    async getDashboardNewAccess() {
        const response = await this.cvApiService.get<boolean>('/api/v2/dashboardnew/dashboardnewaccess');
        if (response.status_code === 200) {
            return response.data;
        } else {
            throw response;
        }
    }

    async copyDashboardFromTemplate(
        dashboardKeyToCopy: string,
        ownerId: number,
        dashboardName: string,
        dashboardGroup: string,
        dashboardDescription: string
    ) {
        const details = {
            OwnerID: ownerId,
            Name: dashboardName,
            Group: dashboardGroup,
            Description: dashboardDescription
        };

        const response = await this.cvApiService.post<Dashboard>(`/api/v2/dashboardCopyFromTemplate/${dashboardKeyToCopy}`, details);
        if (response.status_code === 200) {
            this.openFromTemplate = response.data;
            return response.data;
        } else {
            throw response;
        }
    }

    async copyDashboardFromTemporaryDashboard(key: string, name: string, folder: string, description: string) {
        const url = '/api/v2/dashboards/copyDashboardFromTemporaryDashboard';
        const response = await this.cvApiService.post<DashboardResponsiveApiModelSparse>(url, {
            Key: key,
            Name: name,
            Group: folder,
            Description: description
        });

        if (response.status_code === 200) {
            return response.data;
        } else {
            throw response;
        }
    }

    async copyDashboardFromSystemTemplate(dashboardKey: string, dashboardName: string, group: string, description: string, iscxone = false) {
        const details = {
            Name: dashboardName,
            Group: group,
            Description: description,
            iscxone: iscxone,
        };

        const response = await this.cvApiService.post<Dashboard>(`/api/v2/dashboardCopyFromSystemTemplate/${dashboardKey}`, details);
        if (response.status_code === 200) {
            this.openFromTemplate = response.data;
            return response.data;
        } else {
            throw response;
        }
    }

    async copyTemporaryDashboardFromSystemTemplate(dashboardKey: string, isCXOne = false) {
        const url = `/api/v2/systemtemplate/temporarydashboardcopyfromsystemtemplate/${dashboardKey}/${isCXOne}`;
        const response = await this.cvApiService.post<Dashboard>(url);
        if (response.status_code === 200) {
            this.openFromTemplate = response.data;
            return response.data;
        } else {
            throw response;
        }
    }

    async updateLastViewDate(dashboardKey: string, viewDate: string) {
        const response = await this.cvApiService.post<Dashboard>(`/api/v2/dashboard/${dashboardKey}/view-date/${viewDate}`);
        if (response.status_code === 200) {
            this.synchronizeDashboard(response.data);
            return response.data;
        } else {
            throw response;
        }
    }

    async publishChanges(dashboardKey: string, iscxone: boolean = false) {
        const response = await this.cvApiService
            .post<DashboardResponsiveApiModelSparse>(`/api/v2/dashboard-update/${dashboardKey}/${iscxone}`);
        if (response.status_code === 200) {
            return response.data;
        } else {
            throw response;
        }
    }

    async updateSystemTemplateFromDashboard(dashboardKey: string) {
        const response = await this.cvApiService
            .post<DashboardResponsiveApiModelSparse>(`/api/v2/systemtemplate/updatesystemtemplate/${dashboardKey}`);
        if (response.status_code === 200) {
            return response.data;
        } else {
            throw response;
        }
    }

    getActiveDashboard(): Dashboard {
        const activeIndex = this.activeDashboard.index;
        return this.dashboards[activeIndex];
    }

    getActiveCXoneDashboard(): DashboardResponsiveApiModel {
        return this.cxoneActiveDashboard;
    }

    async openDashboardToWorkspace(workspaceKey: string, dashboard: Dashboard) {
        // Remove the new tab page if it is there (should be at the end)
        for (let i = this.dashboards.length - 1; i >= 0; i--) {
            if (this.dashboards[i] == null) {
                this.dashboards.splice(i, 1);
                break;
            }
        }

        // to call onActivated
        const prevDashboardKey = this.activeDashboard?.key;
        this.triggerActiveDashboardChanged(dashboard.DashboardKey, prevDashboardKey);

        // Set the new dashboard to active
        this.activeDashboard.key = dashboard.DashboardKey;
        await this.saveActiveDashboard(workspaceKey);
    }

    synchronizeDashboard(masterDashboard: Dashboard) {
        if (this.isDashboardInWorkspace(masterDashboard.DashboardID)) {
            // Overwrite the dashboard in the workspace
            for (let i = 0; i < this.dashboards.length; i++) {
                const dashboard = this.dashboards[i];
                if (dashboard?.DashboardID === masterDashboard.DashboardID) {
                    const dashboardToUpdate = dashboard;
                    for (const key in dashboardToUpdate) {
                        if (masterDashboard.hasOwnProperty(key))
                            dashboardToUpdate[key] = deepcopy(masterDashboard[key]);
                    }
                    this.dashboards[i] = dashboardToUpdate;
                }
            }
        }

        // Overwrite the dashboard in the available dashboards
        for (let i = 0; i < this.availableDashboards.length; i++) {
            const dashboard = this.availableDashboards[i];
            if (dashboard.DashboardID === masterDashboard.DashboardID ||
                dashboard.RelatedDashboardID === masterDashboard.DashboardID) {
                const dashboardToUpdate = this.availableDashboards[i];
                for (const key in dashboardToUpdate) {
                    if (masterDashboard.hasOwnProperty(key)) {
                        if (!(key.indexOf('Shared') !== -1)) {
                            switch (key) {
                                case "DashboardID":
                                    if (dashboardToUpdate.RelatedDashboardID === 0)
                                        dashboardToUpdate[key] = deepcopy(masterDashboard[key]);
                                    break;
                                case "RelatedDashboardID":
                                    if (dashboardToUpdate.DashboardID !== masterDashboard["DashboardID"])
                                        dashboardToUpdate[key] = deepcopy(masterDashboard["DashboardID"]);
                                    break;
                                case "DashboardKey":
                                    if (dashboardToUpdate[0] === 0)
                                        dashboardToUpdate[key] = deepcopy(masterDashboard[key]);
                                    break;
                                default:
                                    dashboardToUpdate[key] = deepcopy(masterDashboard[key]);
                                    break;
                            }
                        }
                    }
                }
                this.availableDashboards[i] = dashboardToUpdate;
            }
        }
    }

    isDashboardInWorkspace(identifier: string | number | Dashboard) {
        const type = typeof identifier;

        for (const dashboard of this.dashboards) {
            if (dashboard == null) // Skip the new tab page
                continue;

            switch (type) {
                case "string": // Dashboard key was given
                    if (dashboard.DashboardKey === identifier)
                        return true;
                    break;
                case "number": // Dashboard ID was given
                    if (dashboard.DashboardID === identifier)
                        return true;
                    if (dashboard.RelatedDashboardID === identifier)
                        return true;
                    break;
                default: // Assume dashboard object was given
                    if (dashboard?.DashboardKey === (identifier as Dashboard)?.DashboardKey)
                        return true;
                    break;
            }
        }

        return false;
    }


    /** Get data from server for all the shared/assigned information for a collection of dashboards */
    public async getDashboardsAssignments(dashboardKeys: string[]) {
        try {
            const url = `/api/v2/dashboards/assignments`;
            const response = await this.cvApiService.post<IDashboardAssignments[]>(url, dashboardKeys);
            if (response.status_code >= 200 && response.status_code < 300)
                return response.data;
            else throw response;
        } catch (err) {
            console.error("Failed to get assignment info for selected dashboards");
            throw err;
        }
    }

    /** Get data from server for all the module information for a collection of dashboards */
    public async getDashboardsModules(dashboardKeys: string[]) {
        try {
            const url = `/api/v2/dashboards/modules`;
            const response = await this.cvApiService.post<IDashboardModules[]>(url, dashboardKeys);

            if (response.status_code >= 200 && response.status_code < 300)
                return response.data;
            else throw response;
        }
        catch (err) {
            console.error("Failed getting module info for selected dashboards");
            throw err;
        }
    }

    /** Call endpoint method which adds dashboards to open list and sets last dashboard as active. */
    public async openDashboards(dashboardKeys: string[]) {
        try {
            const url = `/api/v2/dashboards/open`;
            const response = await this.cvApiService.post(url, dashboardKeys);

            if (response.status_code >= 200 && response.status_code < 300)
                return response;
            else throw response;
        } catch (err) {
            console.error("Failed to open dashboard.");
            throw err;
        }
    }

    /** Call endpoint method which adds demo dashboards to open list and sets last dashboard as active. */
    public async canResetDemo() {
        try {
            const url = `/api/v2/dashboards/canResetDemo`;
            const response = await this.cvApiService.get<boolean>(url);

            if (response.status_code >= 200 && response.status_code < 300)
                return response.data;
            else throw response;
        } catch (err) {
            console.error("Failed to get demo permission.");
            throw err;
        }
    }


    /** Call endpoint method which clears the workspace and adds demo dashboards to open list and sets last dashboard as active. */
    public async resetDemo() {
        try {
            // 1. Clear Workspace
            this.clearDashboardsFromWorkspace();
            $("#upgLoad").show();

            // 2. Reset Demo on server
            const url = `/api/v2/dashboards/resetDemo`;
            const response = await this.cvApiService.post<DashboardResponsiveApiModelSparse[]>(url);
            if (response.status_code >= 200 && response.status_code < 300) {
                // 3. Remove previously copied demo dashboards
                this.availableDashboards = this.availableDashboards.filter(d => !d.DashboardKey.endsWith('_DEMO'));
                this.availableDashboardsChanged.emit();

                // 4. Add new demo dashboards
                this.availableDashboards.push(...response.data);

                // 5. Open new demo dashboards
                if (response.data.length > 0) {
                    for (let i = 0; i < response.data.length; i++)
                        this.addDashboardToWorkspace(this.workspaceKey, response.data[i], false, i === response.data.length - 1);
                } else {
                    this.activeDashboard.key = null;
                    this.dashboards.push(null);
                }

                $("#upgLoad").hide();
                return response.data;
            }
            else throw response;
        } catch (err) {
            this.activeDashboard.key = null;
            this.dashboards.push(null);
            $("#upgLoad").hide();
            console.error("Failed to reset demo.");
            throw err;
        }
    }

    /**
     * Change group/folder which a dashboard belongs to
     * Tries to change the group/folder of all selected dashboards. Throws error if
     *      User does not have permission to edit one or more selected dashboard(s).
     *      One of the selected dashboard does not exist.
     *      Server failed update one of the selected dashboard.
     *
     * @param dashboardKeys
     * @param group
     * */
    public async moveDashboards(dashboardKeys: string[], group: string) {
        if (dashboardKeys.length < 1) return;

        try {
            const url = `/api/v2/dashboards/moveto/${encodeURIComponent(group)}`;
            const response = await this.cvApiService.post(url, dashboardKeys);

            if (response.status_code >= 200 && response.status_code < 300) {
                if (!this.dashboardGroups.includes(group))
                    this.dashboardGroups.push(group);
                return response.data;
            }
            else throw response.warnings[0];
        } catch (err) {
            console.error("Failed to move selected dashboards");
            throw err;
        }
    }

    /**
     * Tries to delete all selected dashboards. Throws error if
     *      User does not have permission to delete one or more selected dashboard(s).
     *      One of the selected dashboard does not exist or has already been deleted.
     *      Server failed delete one of the selected dashboard.
     *
     * @param dashboardKeys
     */
    public async deleteDashboards(dashboardKeys: string[], iscxone: boolean = false) {
        if (dashboardKeys.length < 1)
            return;

        try {
            const url = `/api/v2/dashboards/delete/${iscxone}`;
            const response = await this.cvApiService.post(url, dashboardKeys);

            if (response.status_code >= 200 && response.status_code < 300) {
                this.availableDashboardsChanged.emit();
                return response;
            }
            else throw response.warnings;
        } catch (err) {
            console.error("Failed to delete selected dashboards");
            throw err;
        }
    }

    /** Get data formatted for export from endpoint for a dashboard */
    public async getReport(dashboardKey: string, format: string | ModuleManagerExportFormat) {
        try {
            const url = `/api/v2/dashboard/${dashboardKey}/report/${format}`;
            const response = await this.cvApiService.get<string>(url);

            if (response.status_code >= 200 && response.status_code < 300)
                return response;
            else throw response.warnings;
        } catch (err) {
            console.error("Failed getting data to export dashboard");
            throw err;
        }
    }

    /** Get list of avaiable date ranges for master filter. */
    public async getAvailableDateRange() {
        try {
            const url = `/api/v2/dashboard-filter/availableDateRanges`;
            const response = await this.cvApiService.get<ModuleManagerDateRangeInfo[]>(url);

            if (response.status_code >= 200 && response.status_code < 300)
                return response.data;
            else throw response.warnings;
        } catch (err) {
            console.error("Failed getting date range for dashboard");
            throw err;
        }
    }

    /**
     * Get previously set settings for master filter
     *
     * @param dashboardKey
     */
    public async getDashboardFilterSettings(dashboardKey: string) {
        try {
            const url = `/api/v2/dashboard-filter/${dashboardKey}/settings`;
            const response = await this.cvApiService.get<string>(url);

            if (response.status_code >= 200 && response.status_code < 300)
                return response.data;
            else
                throw response.warnings;
        } catch (err) {
            console.error("Failed getting previously applied settings.");
            throw err;
        }
    }

    /**
     * Save changes in the master filter
     *
     * @param dashboardKey
     * @param settings
     */
    public async setDashboardFilterSettings(dashboardKey: string, settings: SlideoutSettingsManager) {
        try {
            const url = `/api/v2/dashboard-filter/${dashboardKey}/settings`;
            const response = await this.cvApiService.post(url, settings.toStateString());

            if (response.status_code >= 200 && response.status_code < 300)
                return true;
            else throw response.warnings;
        } catch (err) {
            console.error("Failed saving applied settings.");
            throw err;
        }
    }

    /**
     * Get previously set settings for CxOne dashboard filter
     *
     * @param dashboardKey
     */
    public async getCxOneDashboardFilters(dashboardKey: string) {
        try {
            const url = `/api/v2/cxone/dashboard-filter/${dashboardKey}/settings`;
            const response = await this.cvApiService.get<string>(url);

            if (response.status_code >= 200 && response.status_code < 300)
                return response.data;
            else
                throw response.warnings;
        } catch (err) {
            console.error("Failed getting previously applied settings.");
            throw err;
        }
    }

    /**
     * Save changes in the CxOne Dashboard filter
     *
     * @param dashboardKey
     * @param settings
     */
    public async setCxOneDashboardFilters(dashboardKey: string, settings: any) {
        try {
            const url = `/api/v2/cxone/dashboard-filter/${dashboardKey}/settings`;
            const response = await this.cvApiService.post(url, JSON.stringify(settings));

            if (response.status_code >= 200 && response.status_code < 300)
                return true;
            else throw response.warnings;
        } catch (err) {
            console.error("Failed saving applied settings.");
            throw err;
        }
    }

    /**
     * Save changes in the CxOne Dashboard filter
     * These changes are applied to the main settings of the dashboard. Not the edit.
     *
     * @param dashboardKey
     * @param settings
     */
    public async setMainCxOneDashboardFilters(dashboardKey: string, settings: any) {
        try {
            const url = `/api/v2/cxone/dashboard-filter/${dashboardKey}/main-settings`;
            const response = await this.cvApiService.post(url, JSON.stringify(settings));

            if (response.status_code >= 200 && response.status_code < 300)
                return true;
            else throw response.warnings;
        } catch (err) {
            console.error("Failed saving applied settings.");
            throw err;
        }
    }

    /** Call endpoint method which removes all dashboards from workspace. */
    public async clearWorkspace(workspaceKey: string, isCxOne = false) {
        try {
            const params = isCxOne ? '?isCxOne=true' : '';
            const url = `/api/v2/workspace/${workspaceKey}/clear${params}`;
            const response = await this.cvApiService.delete<void>(url);

            if (response.status_code >= 200 && response.status_code < 300)
                return response;
            else throw response;
        } catch (err) {
            console.error("Failed to clear workspace of opened dashboards on server");
            throw err;
        }
    }

    /**
     * Adds a placeholder/null dashboard to the user's workspace, in preparation of redirecting them to DashboardNew.aspx.
     * The intention is to skip them to the Create New Dashboard view when they're not already on DashboardNew.aspx.
     */
    public async postAddPlaceholderDashboardToWorkspace() {
        const response = await this.cvApiService.post<boolean>("/api/v2/workspace/add-placeholder");
        if (response?.data !== true) {
            console.log("Could not add a placeholder dashboard to the user's workspace.  Response:", response);
            throw response;
        }
    }

    public clearDashboardsFromWorkspace() {
        this.dashboards.splice(0, this.dashboards.length);
    }

    public async setPublic(dashboardID: number, value: boolean) {
        try {
            const url = `/api/v2/dashboard/${dashboardID}/public/${value}`;
            const response = await this.cvApiService.put<DashboardResponsiveApiModelSparse>(url);
            if (response.status_code >= 200 && response.status_code < 300)
                return response;
            else throw response;
        } catch (err) {
            console.error("Failed to update dashboard");
            throw err;
        }
    }

    public async setFavorite(dashboardID: number, value: boolean) {
        try {
            const url = `/api/v2/dashboard/${dashboardID}/favorite/${value}`;
            const response = await this.cvApiService.put<DashboardResponsiveApiModelSparse>(url);
            if (response.status_code >= 200 && response.status_code < 300)
                return response.data;

            else throw response;
        } catch (err) {
            console.error("Failed to clear workspace of opened dashboards on server");
            throw err;
        }
    }

    protected setAngularJSInjector($injector: ng.auto.IInjectorService) {
        this.$injector = $injector;
    }

    protected setAngularComponentFactoryResolver(componentFactoryResolver: ComponentFactoryResolver) {
        this.componentFactoryResolver = componentFactoryResolver;
    }
}
